import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Button, Checkbox, Input, Select } from '../../../../components';
import type { SelectOption } from '../../../../components';
import { sharedService } from '../../../../services/sharedService';
import { useSharedStore } from '../../../../store/sharedStore';
import {
  AGE_OPTIONS,
  INITIAL_USER_ADVANCED_FILTERS,
  MANGLIK_PREFERENCE_OPTIONS,
  type UserAdvancedFilters,
} from '../../userSearchFilters';
import './UserMoreFiltersModal.scss';

interface DynamicReligionFields {
  showSect: boolean;
  showCaste: boolean;
  showJamat: boolean;
}

const initialDynamicFields: DynamicReligionFields = {
  showSect: false,
  showCaste: false,
  showJamat: false,
};

export interface UserMoreFiltersModalProps {
  isOpen: boolean;
  filters: UserAdvancedFilters;
  onClose: () => void;
  onApply: (filters: UserAdvancedFilters) => void;
  onClear: () => void;
}

export const UserMoreFiltersModal: React.FC<UserMoreFiltersModalProps> = ({
  isOpen,
  filters,
  onClose,
  onApply,
  onClear,
}) => {
  const [draft, setDraft] = useState<UserAdvancedFilters>(filters);
  const [dynamicFields, setDynamicFields] = useState<DynamicReligionFields>(initialDynamicFields);
  const [incomeCurrencies, setIncomeCurrencies] = useState<SelectOption[]>([]);

  const [isLoadingSects, setIsLoadingSects] = useState(false);
  const [isLoadingCastes, setIsLoadingCastes] = useState(false);
  const [isLoadingJamats, setIsLoadingJamats] = useState(false);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isLoadingOccupations, setIsLoadingOccupations] = useState(false);

  const {
    motherTongues,
    heights,
    religions,
    maritalStatuses,
    complexions,
    bloodGroups,
    disabilities,
    sects,
    castes,
    jamats,
    countries,
    states,
    cities,
    degrees,
    employedIn,
    occupations,
    drinkingHabits,
    smokingHabits,
    diets,
    hobbies,
    fetchSectsWithMeta,
    fetchCastes,
    fetchJamats,
    fetchStates,
    fetchCities,
    fetchOccupations,
    getReligionMeta,
    getSectMeta,
    clearDependentLookups,
  } = useSharedStore();

  const updateDraft = useCallback((patch: Partial<UserAdvancedFilters>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const loadReligionLookups = useCallback(
    async (religionId: string, sectId?: string) => {
      const religionMeta = getReligionMeta(religionId);
      if (!religionMeta) {
        setDynamicFields(initialDynamicFields);
        return;
      }

      const { have_sect, have_caste } = religionMeta;

      if (have_sect) {
        setDynamicFields({ showSect: true, showCaste: false, showJamat: false });
        setIsLoadingSects(true);
        try {
          await fetchSectsWithMeta(religionId);
        } finally {
          setIsLoadingSects(false);
        }

        if (sectId) {
          const sectMeta = getSectMeta(sectId);
          const hasJamat = sectMeta?.have_jamat ?? false;
          setDynamicFields({ showSect: true, showCaste: true, showJamat: hasJamat });

          setIsLoadingCastes(true);
          try {
            await fetchCastes(religionId, sectId);
          } finally {
            setIsLoadingCastes(false);
          }

          if (hasJamat) {
            setIsLoadingJamats(true);
            try {
              await fetchJamats(sectId);
            } finally {
              setIsLoadingJamats(false);
            }
          }
        }
      } else if (have_caste) {
        setDynamicFields({ showSect: false, showCaste: true, showJamat: false });
        setIsLoadingCastes(true);
        try {
          await fetchCastes(religionId);
        } finally {
          setIsLoadingCastes(false);
        }
      } else {
        setDynamicFields(initialDynamicFields);
      }
    },
    [fetchCastes, fetchJamats, fetchSectsWithMeta, getReligionMeta, getSectMeta]
  );

  const handleReligionChange = useCallback(
    async (religionId: string) => {
      updateDraft({
        religionId,
        sectId: '',
        jamatId: '',
        casteId: '',
        haveCasteBar: false,
      });

      clearDependentLookups('sects');
      clearDependentLookups('sectsWithMeta');
      clearDependentLookups('castes');
      clearDependentLookups('jamats');

      if (!religionId) {
        setDynamicFields(initialDynamicFields);
        return;
      }

      await loadReligionLookups(religionId);
    },
    [clearDependentLookups, loadReligionLookups, updateDraft]
  );

  const handleSectChange = useCallback(
    async (sectId: string) => {
      updateDraft({
        sectId,
        casteId: '',
        jamatId: '',
        haveCasteBar: false,
      });

      clearDependentLookups('castes');
      clearDependentLookups('jamats');

      if (!sectId || !draft.religionId) {
        setDynamicFields((prev) => ({ ...prev, showCaste: false, showJamat: false }));
        return;
      }

      const sectMeta = getSectMeta(sectId);
      const hasJamat = sectMeta?.have_jamat ?? false;
      setDynamicFields((prev) => ({ ...prev, showCaste: true, showJamat: hasJamat }));

      setIsLoadingCastes(true);
      try {
        await fetchCastes(draft.religionId, sectId);
      } finally {
        setIsLoadingCastes(false);
      }

      if (hasJamat) {
        setIsLoadingJamats(true);
        try {
          await fetchJamats(sectId);
        } finally {
          setIsLoadingJamats(false);
        }
      }
    },
    [
      clearDependentLookups,
      draft.religionId,
      fetchCastes,
      fetchJamats,
      getSectMeta,
      updateDraft,
    ]
  );

  const handleCountryChange = useCallback(
    async (countryId: string) => {
      updateDraft({ countryId, stateId: '', cityId: '' });
      clearDependentLookups('states');
      clearDependentLookups('cities');

      if (!countryId) return;

      setIsLoadingStates(true);
      try {
        await fetchStates(countryId);
      } finally {
        setIsLoadingStates(false);
      }
    },
    [clearDependentLookups, fetchStates, updateDraft]
  );

  const handleStateChange = useCallback(
    async (stateId: string) => {
      updateDraft({ stateId, cityId: '' });
      clearDependentLookups('cities');

      if (!stateId) return;

      setIsLoadingCities(true);
      try {
        await fetchCities(stateId);
      } finally {
        setIsLoadingCities(false);
      }
    },
    [clearDependentLookups, fetchCities, updateDraft]
  );

  const handleEmployedInChange = useCallback(
    async (employedInId: string) => {
      updateDraft({ employedInId, occupationId: '' });
      clearDependentLookups('occupations');

      if (!employedInId) return;

      setIsLoadingOccupations(true);
      try {
        await fetchOccupations(employedInId);
      } finally {
        setIsLoadingOccupations(false);
      }
    },
    [clearDependentLookups, fetchOccupations, updateDraft]
  );

  const handleMinAgeChange = useCallback(
    (minAge: string) => {
      setDraft((prev) => {
        const min = minAge ? Number(minAge) : null;
        const max = prev.maxAge ? Number(prev.maxAge) : null;
        return {
          ...prev,
          minAge,
          maxAge: min !== null && max !== null && max < min ? '' : prev.maxAge,
        };
      });
    },
    []
  );

  const handleMinHeightChange = useCallback(
    (minHeightId: string) => {
      setDraft((prev) => {
        const min = minHeightId ? Number(minHeightId) : null;
        const max = prev.maxHeightId ? Number(prev.maxHeightId) : null;
        return {
          ...prev,
          minHeightId,
          maxHeightId:
            min !== null && max !== null && max < min ? '' : prev.maxHeightId,
        };
      });
    },
    []
  );

  const handleHobbyToggle = useCallback((hobbyId: string, checked: boolean) => {
    setDraft((prev) => {
      const next = new Set(prev.hobbyIds);
      if (checked) {
        next.add(hobbyId);
      } else {
        next.delete(hobbyId);
      }
      return { ...prev, hobbyIds: Array.from(next) };
    });
  }, []);

  const maxAgeOptions = useMemo(() => {
    if (!draft.minAge) return AGE_OPTIONS;
    const min = Number(draft.minAge);
    return AGE_OPTIONS.filter((opt) => Number(opt.value) >= min);
  }, [draft.minAge]);

  const maxHeightOptions = useMemo(() => {
    if (!draft.minHeightId) return heights;
    const min = Number(draft.minHeightId);
    return heights.filter((opt) => Number(opt.value) >= min);
  }, [draft.minHeightId, heights]);

  useEffect(() => {
    if (!isOpen) return;
    setDraft({ ...filters });

    const loadDependentLookups = async () => {
      if (filters.religionId) {
        await loadReligionLookups(filters.religionId, filters.sectId || undefined);
      } else {
        setDynamicFields(initialDynamicFields);
      }

      if (filters.countryId) {
        setIsLoadingStates(true);
        try {
          await fetchStates(filters.countryId);
        } finally {
          setIsLoadingStates(false);
        }
      }

      if (filters.stateId) {
        setIsLoadingCities(true);
        try {
          await fetchCities(filters.stateId);
        } finally {
          setIsLoadingCities(false);
        }
      }

      if (filters.employedInId) {
        setIsLoadingOccupations(true);
        try {
          await fetchOccupations(filters.employedInId);
        } finally {
          setIsLoadingOccupations(false);
        }
      }
    };

    void loadDependentLookups();
  }, [
    isOpen,
    filters,
    fetchCities,
    fetchOccupations,
    fetchStates,
    loadReligionLookups,
  ]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const currencies = await sharedService.getCurrencies();
        setIncomeCurrencies(currencies);
      } catch (err) {
        console.error('Failed to fetch income currencies:', err);
      }
    };
    void fetchCurrencies();
  }, []);

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  const handleClear = () => {
    setDraft({ ...INITIAL_USER_ADVANCED_FILTERS });
    setDynamicFields(initialDynamicFields);
    onClear();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="user-more-filters-modal" role="presentation">
      <div
        className="user-more-filters-modal__overlay"
        onClick={onClose}
        aria-hidden="true"
      />

      <section
        className="user-more-filters-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-more-filters-title"
      >
        <header className="user-more-filters-modal__header">
          <div>
            <h2 id="user-more-filters-title">More Filters</h2>
            <p>Refine profiles using advanced search criteria</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <Cross2Icon />
          </button>
        </header>

        <div className="user-more-filters-modal__body">
          <section className="user-more-filters-modal__section">
            <h3 className="user-more-filters-modal__section-title">Basic Information</h3>
            <div className="user-more-filters-modal__ranges">
              <div className="user-more-filters-modal__range-row">
                <Select
                  label="Min Age"
                  placeholder="Min Age"
                  value={draft.minAge}
                  onChange={handleMinAgeChange}
                  options={AGE_OPTIONS}
                  searchable={false}
                />
                <span>–</span>
                <Select
                  label="Max Age"
                  placeholder="Max Age"
                  value={draft.maxAge}
                  onChange={(value) => updateDraft({ maxAge: value })}
                  options={maxAgeOptions}
                  searchable={false}
                />
              </div>

              <div className="user-more-filters-modal__range-row">
                <Select
                  label="Min Height"
                  placeholder="Min Height"
                  value={draft.minHeightId}
                  onChange={handleMinHeightChange}
                  options={heights}
                  searchable
                />
                <span>–</span>
                <Select
                  label="Max Height"
                  placeholder="Max Height"
                  value={draft.maxHeightId}
                  onChange={(value) => updateDraft({ maxHeightId: value })}
                  options={maxHeightOptions}
                  searchable
                />
              </div>
            </div>
          </section>

          <section className="user-more-filters-modal__section">
            <h3 className="user-more-filters-modal__section-title">Personal Details</h3>
            <div className="user-more-filters-modal__grid">
              <Select
                label="Marital Status"
                placeholder="Select Marital Status"
                value={draft.maritalStatusId}
                onChange={(value) => updateDraft({ maritalStatusId: value })}
                options={maritalStatuses}
                searchable
              />
              <Select
                label="Mother Tongue"
                placeholder="Select Mother Tongue"
                value={draft.motherTongueId}
                onChange={(value) => updateDraft({ motherTongueId: value })}
                groups={motherTongues}
                searchable
              />
              <Select
                label="Religion"
                placeholder="Select Religion"
                value={draft.religionId}
                onChange={(value) => void handleReligionChange(value)}
                options={religions}
                searchable
              />

              {dynamicFields.showSect && (
                <Select
                  label="Sect"
                  placeholder="Select Sect"
                  value={draft.sectId}
                  onChange={(value) => void handleSectChange(value)}
                  options={sects}
                  disabled={isLoadingSects}
                  searchable
                />
              )}

              {dynamicFields.showJamat && (
                <Select
                  label="Jamat"
                  placeholder="Select Jamat"
                  value={draft.jamatId}
                  onChange={(value) => updateDraft({ jamatId: value })}
                  options={jamats}
                  disabled={isLoadingJamats}
                  searchable
                />
              )}

              {dynamicFields.showCaste && (
                <Select
                  label="Caste"
                  placeholder="Select Caste"
                  value={draft.casteId}
                  onChange={(value) => updateDraft({ casteId: value })}
                  options={castes}
                  disabled={isLoadingCastes}
                  searchable
                />
              )}

              {dynamicFields.showCaste && (
                <Checkbox
                  label="Have caste bar"
                  checked={draft.haveCasteBar}
                  onChange={(checked) => updateDraft({ haveCasteBar: checked })}
                />
              )}

              <Select
                label="Manglik Preference"
                placeholder="Select Preference"
                value={draft.manglikPreference}
                onChange={(value) => updateDraft({ manglikPreference: value })}
                options={MANGLIK_PREFERENCE_OPTIONS}
                searchable={false}
              />
            </div>
          </section>

          <section className="user-more-filters-modal__section">
            <h3 className="user-more-filters-modal__section-title">Location & Education</h3>
            <div className="user-more-filters-modal__grid">
              <Select
                label="Country"
                placeholder="Select Country"
                value={draft.countryId}
                onChange={(value) => void handleCountryChange(value)}
                options={countries}
                searchable
              />
              <Select
                label="State"
                placeholder="Select State"
                value={draft.stateId}
                onChange={(value) => void handleStateChange(value)}
                options={states}
                disabled={!draft.countryId || isLoadingStates}
                searchable
              />
              <Select
                label="City"
                placeholder="Select City"
                value={draft.cityId}
                onChange={(value) => updateDraft({ cityId: value })}
                options={cities}
                disabled={!draft.stateId || isLoadingCities}
                searchable
              />
              <Select
                label="Highest Degree"
                placeholder="Select Degree"
                value={draft.highestDegreeId}
                onChange={(value) => updateDraft({ highestDegreeId: value })}
                groups={degrees}
                searchable
              />
              <Select
                label="Employed In"
                placeholder="Select Employed In"
                value={draft.employedInId}
                onChange={(value) => void handleEmployedInChange(value)}
                options={employedIn}
                searchable
              />
              <Select
                label="Occupation"
                placeholder="Select Occupation"
                value={draft.occupationId}
                onChange={(value) => updateDraft({ occupationId: value })}
                groups={occupations}
                disabled={!draft.employedInId || isLoadingOccupations}
                searchable
              />

              <div className="user-more-filters-modal__income-row">
                <Select
                  label="Currency"
                  placeholder="Currency"
                  value={draft.incomeCurrencyId}
                  onChange={(value) => updateDraft({ incomeCurrencyId: value })}
                  options={incomeCurrencies}
                  searchable={false}
                />
                <Input
                  label="Min Annual Income"
                  type="number"
                  placeholder="Min Annual Income"
                  value={draft.minAnnualIncome}
                  onChange={(e) => updateDraft({ minAnnualIncome: e.target.value })}
                  min={0}
                />
              </div>
            </div>
          </section>

          <section className="user-more-filters-modal__section">
            <h3 className="user-more-filters-modal__section-title">Physical Attributes</h3>
            <div className="user-more-filters-modal__grid">
              <div className="user-more-filters-modal__range-row">
                <Input
                  label="Min Weight (kg)"
                  type="number"
                  placeholder="Min Weight"
                  value={draft.minWeight}
                  onChange={(e) => updateDraft({ minWeight: e.target.value })}
                  min={0}
                />
                <span>–</span>
                <Input
                  label="Max Weight (kg)"
                  type="number"
                  placeholder="Max Weight"
                  value={draft.maxWeight}
                  onChange={(e) => updateDraft({ maxWeight: e.target.value })}
                  min={0}
                />
              </div>

              <Select
                label="Complexion"
                placeholder="Select Complexion"
                value={draft.complexionId}
                onChange={(value) => updateDraft({ complexionId: value })}
                options={complexions}
                searchable
              />
              <Select
                label="Blood Group"
                placeholder="Select Blood Group"
                value={draft.bloodGroupId}
                onChange={(value) => updateDraft({ bloodGroupId: value })}
                options={bloodGroups}
                searchable={false}
              />
              <Select
                label="Disability"
                placeholder="Select Disability"
                value={draft.disabilityId}
                onChange={(value) => updateDraft({ disabilityId: value })}
                options={disabilities}
                searchable
              />
            </div>
          </section>

          <section className="user-more-filters-modal__section">
            <h3 className="user-more-filters-modal__section-title">Lifestyle & Interests</h3>
            <div className="user-more-filters-modal__grid">
              <Select
                label="Drinking Habit"
                placeholder="Select Drinking Habit"
                value={draft.drinkingHabitId}
                onChange={(value) => updateDraft({ drinkingHabitId: value })}
                options={drinkingHabits}
                searchable={false}
              />
              <Select
                label="Smoking Habit"
                placeholder="Select Smoking Habit"
                value={draft.smokingHabitId}
                onChange={(value) => updateDraft({ smokingHabitId: value })}
                options={smokingHabits}
                searchable={false}
              />
              <Select
                label="Diet"
                placeholder="Select Diet"
                value={draft.dietId}
                onChange={(value) => updateDraft({ dietId: value })}
                options={diets}
                searchable={false}
              />

              <div className="user-more-filters-modal__hobbies">
                {hobbies.map((hobby) => (
                  <Checkbox
                    key={hobby.value}
                    label={hobby.label}
                    checked={draft.hobbyIds.includes(hobby.value)}
                    onChange={(checked) => handleHobbyToggle(hobby.value, checked)}
                  />
                ))}
              </div>
            </div>
          </section>
        </div>

        <footer className="user-more-filters-modal__footer">
          <Button variant="text" onClick={handleClear}>
            Clear filters
          </Button>
          <div className="user-more-filters-modal__footer-actions">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              className="user-more-filters-modal__apply-btn"
              onClick={handleApply}
            >
              Apply filters
            </Button>
          </div>
        </footer>
      </section>
    </div>
  );
};
